import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GraduationCap, Mail, LogOut } from 'lucide-react';

export function ProtectedRoute() {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div
        data-testid="auth-loading-screen"
        style={{
          height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#F8F9FB', flexDirection: 'column', gap: '1rem',
        }}
      >
        <div style={{
          width: 40, height: 40, border: '3px solid #E2E8F0',
          borderTopColor: '#1E2140', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ fontFamily: 'Nunito, sans-serif', color: '#64748B', fontSize: '0.9rem' }}>
          Loading...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.access_status === 'trial_expired') {
    return <TrialExpiredScreen user={user} onLogout={logout} />;
  }

  if (user.access_status === 'demo_expired') {
    return <DemoExpiredScreen user={user} onLogout={logout} />;
  }

  return <Outlet />;
}

function DemoExpiredScreen({ user, onLogout }) {
  return (
    <div
      data-testid="demo-expired-screen"
      style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F9FB' }}
    >
      <div style={{ textAlign: 'center', maxWidth: 480, padding: '2.5rem 2rem' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '0.875rem', background: '#1E2140',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.75rem',
        }}>
          <GraduationCap size={28} color="white" />
        </div>
        <h1 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.625rem', fontWeight: 700, color: '#1A1D20', marginBottom: '0.625rem' }}>
          Your demo has ended
        </h1>
        <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B', lineHeight: 1.7, marginBottom: '1.75rem' }}>
          Thank you for trying FrenchBoard{user.school_name ? ` with ${user.school_name}` : ''}.
          To continue using the platform, please reach out and we&apos;ll get you set up.
        </p>
        <a
          href="mailto:base@whitelane.com?subject=Continuing%20after%20demo"
          data-testid="demo-expired-contact"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.875rem 2rem', borderRadius: '0.75rem',
            background: '#1E2140', color: 'white', textDecoration: 'none',
            fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 600,
          }}
        >
          <Mail size={18} /> Get in touch
        </a>
        <div style={{ marginTop: '1.25rem' }}>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8' }}>base@whitelane.com</span>
        </div>
        <button
          onClick={onLogout}
          data-testid="demo-expired-logout"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
            marginTop: '2rem', background: 'none', border: 'none',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
            color: '#94A3B8', cursor: 'pointer', padding: '0.25rem',
          }}
        >
          <LogOut size={14} /> Sign in with a different account
        </button>
      </div>
    </div>
  );
}

function TrialExpiredScreen({ user, onLogout }) {
  const trialEnd = user.trial_end ? new Date(user.trial_end) : null;
  const trialDays = user.trial_days || '';

  return (
    <div
      data-testid="trial-expired-screen"
      style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#F8F9FB',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 480, padding: '2.5rem 2rem' }}>
        {/* Logo */}
        <div style={{
          width: 56, height: 56, borderRadius: '0.875rem', background: '#1E2140',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.75rem',
        }}>
          <GraduationCap size={28} color="white" />
        </div>

        <h1 style={{
          fontFamily: 'Outfit, sans-serif', fontSize: '1.625rem', fontWeight: 700,
          color: '#1A1D20', marginBottom: '0.625rem',
        }}>
          Your evaluation period has ended
        </h1>

        <p style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#64748B',
          lineHeight: 1.7, marginBottom: '0.25rem',
        }}>
          Thank you for exploring FrenchBoard
          {user.school_name ? ` with ${user.school_name}` : ''}.
        </p>

        {trialEnd && (
          <p style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8',
            marginBottom: '1.75rem',
          }}>
            Your {trialDays}-day trial concluded on {trialEnd.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}.
          </p>
        )}

        <p style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', color: '#475569',
          lineHeight: 1.7, marginBottom: '2rem',
        }}>
          To continue with full access to the curriculum-aligned content and teacher tools,
          we'd be happy to get you set up with a subscription.
        </p>

        <a
          href="mailto:base@whitelane.com?subject=Subscription%20inquiry"
          data-testid="trial-expired-contact"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.875rem 2rem', borderRadius: '0.75rem',
            background: '#1E2140', color: 'white', textDecoration: 'none',
            fontFamily: 'Outfit, sans-serif', fontSize: '0.95rem', fontWeight: 600,
            transition: 'opacity 0.2s', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <Mail size={18} />
          Get in touch
        </a>

        <div style={{ marginTop: '1.25rem' }}>
          <span style={{
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', color: '#94A3B8',
          }}>
            base@whitelane.com
          </span>
        </div>

        <button
          onClick={onLogout}
          data-testid="trial-expired-logout"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
            marginTop: '2rem', background: 'none', border: 'none',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem',
            color: '#94A3B8', cursor: 'pointer', padding: '0.25rem',
          }}
        >
          <LogOut size={14} />
          Sign in with a different account
        </button>
      </div>
    </div>
  );
}
